import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ATTRIBUTES } from "@/constants/contentTestIds";
import FieldSchemaEditor from "@/components/FieldSchemaEditor";/**
 * Per-brand schema editor for entity-typed additional attributes.
 * Currently exposes "location" by default and "user" as an optional tab for
 * forward-compatibility (admins can already define user attributes; the user
 * detail UI that consumes them ships in a later milestone).
 */
export default function BrandAttributes() {
  const { brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [entityType, setEntityType] = useState("location");
  const [schema, setSchema] = useState({ fields: [] });
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    const [{ data: b }, { data: s }] = await Promise.all([
      api.get(`/brands/${brandId}`),
      api.get(`/brands/${brandId}/attribute-schemas/${entityType}`),
    ]);
    setBrand(b);
    setSchema(s);
  };
  useEffect(() => { load(); }, [brandId, entityType]);

  const save = async () => {
    setError("");
    try {
      await api.put(`/brands/${brandId}/attribute-schemas/${entityType}`, {
        fields: schema.fields,
      });
      setSavedAt(new Date().toISOString());
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid={ATTRIBUTES.pageRoot} className="max-w-5xl">
      <Link to={`/brands/${brandId}`} className="text-xs text-[#6B6358]">&larr; Back to brand</Link>
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mt-4 mb-2">
        {brand?.name || "Brand"} &middot; Custom Attributes
      </p>
      <h1 className="font-serif text-5xl tracking-tight">Custom Attributes</h1>
      <p className="text-base text-[#3D3A35] mt-3 max-w-2xl">
        Define brand-specific additional attributes for your locations now —
        and for your users when that workflow ships. The same Custom Fields
        engine powers both.
      </p>

      <div className="mt-8 flex gap-1 border-b border-[#1A1F2C]/10">
        <Tab active={entityType === "location"} onClick={() => setEntityType("location")}>
          Locations
        </Tab>
        <Tab active={entityType === "user"} onClick={() => setEntityType("user")}>
          Users <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#B5462F]">forward-compat</span>
        </Tab>
      </div>

      <div className="mt-8">
        <FieldSchemaEditor
          fields={schema.fields || []}
          onChange={(fields) => setSchema({ ...schema, fields })}
          testidPrefix={ATTRIBUTES.fieldRowPrefix}
        />
        <div className="flex items-center gap-3 mt-6">
          <button
            data-testid={ATTRIBUTES.saveSchema}
            onClick={save}
            className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium"
          >
            Save schema
          </button>
          {savedAt && <span className="text-xs text-[#6B6358]">Saved.</span>}
          {error && <span className="text-sm text-[#B5462F]">{error}</span>}
        </div>
      </div>
    </div>
  );
}

function Tab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-3 text-sm font-medium border-b-2 ${
        active ? "border-[#B5462F] text-[#1A1F2C]" : "border-transparent text-[#6B6358] hover:text-[#1A1F2C]"
      }`}
    >
      {children}
    </button>
  );
}
